import type { WizardMode } from "@/lib/onboarding/coach-store";

/** Everything the coach needs to decide which tip to show right now. */
export interface CoachContext {
  pathname: string;
  eventId: string | null;
  eventsUsed: number;
  wizardMode: WizardMode | null;
  wizardStep: number;
  completedSteps: Record<string, string>;
  /** User explicitly re-armed the tour (user-settings → "Restart intro"). */
  restarted: boolean;
}

export interface CoachStop {
  id: string;
  /** Existing scene riv under /public/riv (rendered via VideoPlayerRiv). */
  riv: string;
  /** i18n keys under the "onboarding.coach" namespace. */
  titleKey: string;
  bodyKey: string;
  /** Optional CSS selector to pulse-ring (resolved with waitForAnchor). */
  highlight?: string;
  /** Optional "Next →" navigation target (event-surface chaining). */
  next?: (eventId: string) => string;
  /** Position within the create-event wizard, for "step {n}/{total}". */
  progress?: { index: number; total: number };
}

const WIZARD_TOTAL = 6;

const onEvent = (ctx: CoachContext, suffix: string) =>
  !!ctx.eventId && ctx.pathname.includes(`/event/${ctx.eventId}${suffix}`);

/**
 * Ordered journey stops; the coach shows the FIRST whose predicate matches the
 * current context. Tips are concise (1–2 lines) and the coach auto-advances as
 * the user scrolls the wizard / navigates event surfaces.
 */
type Rule = { stop: CoachStop; match: (ctx: CoachContext) => boolean };

const RULES: Rule[] = [
  // ── Create-event wizard ────────────────────────────────────────────────
  // Chat-first view (Velo interview + live preview) — one gentle tip.
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "chat",
    stop: { id: "wizardChat", riv: "CreatingEvent", titleKey: "wizardChat.title", bodyKey: "wizardChat.body" },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "choose",
    stop: { id: "wizardChoose", riv: "CreatingEvent", titleKey: "wizardChoose.title", bodyKey: "wizardChoose.body", highlight: '[data-coach-anchor="wizard-choose-scratch"]' },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "template",
    stop: { id: "wizardTemplate", riv: "CreatingEvent", titleKey: "wizardTemplate.title", bodyKey: "wizardTemplate.body" },
  },
  // Direction mode reuses the scratch wizard's steps; only the extra Package
  // review step (index 6) gets its own tip.
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "direction" && c.wizardStep === 6,
    stop: { id: "wizardPackage", riv: "CreatingEvent", titleKey: "wizardPackage.title", bodyKey: "wizardPackage.body", highlight: '[data-coach-anchor="wizard-package"]' },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "direction",
    stop: { id: "wizardDirection", riv: "CreatingEvent", titleKey: "wizardDirection.title", bodyKey: "wizardDirection.body" },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 0,
    stop: { id: "wizardGeneral", riv: "CreatingEvent", titleKey: "wizardGeneral.title", bodyKey: "wizardGeneral.body", highlight: '[data-coach-anchor="wizard-general"]', progress: { index: 1, total: WIZARD_TOTAL } },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 1,
    stop: { id: "wizardLocation", riv: "NoRoom", titleKey: "wizardLocation.title", bodyKey: "wizardLocation.body", highlight: '[data-coach-anchor="wizard-location"]', progress: { index: 2, total: WIZARD_TOTAL } },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 2,
    stop: { id: "wizardRegistration", riv: "CreateLandingpage", titleKey: "wizardRegistration.title", bodyKey: "wizardRegistration.body", highlight: '[data-coach-anchor="wizard-registration"]', progress: { index: 3, total: WIZARD_TOTAL } },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 3,
    stop: { id: "wizardRoles", riv: "NoGuests", titleKey: "wizardRoles.title", bodyKey: "wizardRoles.body", highlight: '[data-coach-anchor="wizard-roles"]', progress: { index: 4, total: WIZARD_TOTAL } },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 4,
    stop: { id: "wizardPlugins", riv: "CreatingEvent", titleKey: "wizardPlugins.title", bodyKey: "wizardPlugins.body", highlight: '[data-coach-anchor="wizard-plugins"]', progress: { index: 5, total: WIZARD_TOTAL } },
  },
  {
    match: (c) => c.pathname === "/create-event" && c.wizardMode === "scratch" && c.wizardStep === 5,
    stop: { id: "wizardCreate", riv: "CreatingEvent", titleKey: "wizardCreate.title", bodyKey: "wizardCreate.body", highlight: '[data-coach-anchor="wizard-collaborators"]', progress: { index: 6, total: WIZARD_TOTAL } },
  },
  // Wizard, mode not yet known (initial paint) — keep a gentle nudge.
  {
    match: (c) => c.pathname === "/create-event",
    stop: { id: "wizardChoose", riv: "CreatingEvent", titleKey: "wizardChoose.title", bodyKey: "wizardChoose.body" },
  },

  // ── Real event surfaces (react to route) ───────────────────────────────
  // Guest list: once a guest exists, flip to a success tip; until then, prompt +
  // pulse the add button.
  {
    match: (c) => onEvent(c, "/guests/guestlist") && !!c.completedSteps["add_first_guest"],
    stop: {
      id: "addGuestDone", riv: "NoGuests", titleKey: "addGuestDone.title", bodyKey: "addGuestDone.body",
      next: (id) => `/event/${id}/website`,
    },
  },
  {
    match: (c) => onEvent(c, "/guests/guestlist"),
    stop: {
      id: "addGuest", riv: "NoGuests", titleKey: "addGuest.title", bodyKey: "addGuest.body",
      highlight: '[data-testid="event-guestlist-create-guest-btn"]',
      next: (id) => `/event/${id}/website`,
    },
  },
  // website / mail / seating tips are gated on their milestone being UNDONE, so
  // a returning user who restarts the tour isn't coached through pages they've
  // already set up — those stops fall through to the "all set" wrap-up instead.
  // (Mirrors how the guest list flips addGuest → addGuestDone above.)
  {
    match: (c) => onEvent(c, "/website") && !c.completedSteps["setup_website"],
    stop: { id: "website", riv: "CreateLandingpage", titleKey: "website.title", bodyKey: "website.body", next: (id) => `/event/${id}/messaging/mail-templates` },
  },
  {
    match: (c) =>
      onEvent(c, "/messaging/mail-templates") &&
      !c.completedSteps["create_email_template"] &&
      !c.completedSteps["send_first_email"],
    stop: { id: "mail", riv: "Mail", titleKey: "mail.title", bodyKey: "mail.body", next: (id) => `/event/${id}/seating` },
  },
  {
    match: (c) => onEvent(c, "/seating") && !c.completedSteps["explore_seating"],
    stop: { id: "seating", riv: "NoRoom", titleKey: "seating.title", bodyKey: "seating.body" },
  },
  // Any other surface of the real event → "all set" wrap-up. NOT on settings
  // screens: a user digging through Settings/Plugins is mid-task (the audit
  // caught "You're all set! 🎉" firing while they hunted for the Website
  // plugin) — celebrating there reads as mockery. Let the coach stay quiet.
  {
    match: (c) =>
      !!c.eventId &&
      c.pathname.includes(`/event/${c.eventId}`) &&
      !c.pathname.includes("/settings"),
    stop: { id: "allSet", riv: "CreatingEvent", titleKey: "allSet.title", bodyKey: "allSet.body" },
  },

  // ── Dashboard root, no event yet → point at New event ──────────────────
  // Gated on eventsUsed===0 so a returning user who already has an event never
  // sees "create your first event" on the dashboard.
  {
    match: (c) =>
      !c.eventId && c.eventsUsed === 0 && (c.pathname === "/" || c.pathname === ""),
    stop: {
      id: "createEvent", riv: "CreatingEvent", titleKey: "createEvent.title", bodyKey: "createEvent.body",
      highlight: '[data-tour-anchor="tour-create-event"]',
    },
  },

  // ── Dashboard root after an explicit RESTART ───────────────────────────
  // A returning user (eventsUsed > 0) who re-armed the tour would otherwise
  // match nothing here — the coach would stay invisible and the restart would
  // feel broken. Greet them and point at the event switcher / create button.
  {
    match: (c) =>
      !c.eventId && c.restarted && (c.pathname === "/" || c.pathname === ""),
    stop: {
      id: "restartDash", riv: "CreatingEvent", titleKey: "restartDash.title", bodyKey: "restartDash.body",
      highlight: '[data-tour-anchor="tour-create-event"]',
    },
  },
];

/** First matching stop for the given context, or null (coach stays hidden). */
export function resolveCoachStop(ctx: CoachContext): CoachStop | null {
  return RULES.find((r) => r.match(ctx))?.stop ?? null;
}
